import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer } from "recharts";
import { CheckCircle2, AlertCircle, Loader2, Send, Info, Shield } from "lucide-react";
import { resolveBackendBase } from "../lib/api";

const API = `${resolveBackendBase()}/api/v2`;

const FLAG_EXPLANATIONS = {
    "SVP": { name: "Strategic Value Premium", desc: "Recognises sole-owner functions, rare expertise, cross-entity reach, or deep institutional knowledge." },
    "BI":  { name: "Builder Inhibitor", desc: "Triggered when manager validation markedly exceeds the technical/hands-on/cadre-band evidence." },
    "CC":  { name: "Coverage Caveat", desc: "Triggered when coverage of the Technical or Hands-On sections falls below the attempted-item thresholds. Not a capability judgment — an observability note." },
    "STP": { name: "Senior-Track Pattern", desc: "Technical Phase 3 (judgment under fire) significantly exceeds Phase 1 (foundational) — consistent with senior-track role expectations." },
    "HOS+": { name: "Hands-On Strength", desc: "Technical Phase 1 significantly exceeds Phase 3 — consistent with high proficiency on foundational work." },
    "MVO-L": { name: "Manager Validation Outlier — Low", desc: "Triggered when manager validation is materially below the technical/hands-on/cadre-band evidence." },
    "RoR": { name: "Right-of-Review Exercised", desc: "Indicates the candidate exercised their right to review their results and rationales." },
};

export default function CandidateReviewPage() {
    const { token } = useParams();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);
    const [contested, setContested] = useState({});
    const [narrative, setNarrative] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        axios.get(`${API}/review/${token}`)
            .then((r) => { setData(r.data); setLoading(false); })
            .catch((e) => { setError(e.response?.data?.detail || "Failed to load review pack"); setLoading(false); });
    }, [token]);

    const toggleContest = (qid, rebuttal) => {
        setContested((c) => {
            const next = { ...c };
            if (rebuttal === null) delete next[qid];
            else next[qid] = rebuttal;
            return next;
        });
    };

    const submit = async () => {
        setSubmitting(true);
        try {
            const items = Object.entries(contested).map(([qid, rebuttal]) => ({ question_id: qid, candidate_rebuttal: rebuttal }));
            await axios.post(`${API}/review/${token}/submit`, {
                contested_items: items,
                overall_narrative: narrative,
            });
            const fresh = await axios.get(`${API}/review/${token}`);
            setData(fresh.data);
        } catch (e) {
            alert("Submit failed: " + (e.response?.data?.detail || e.message));
        }
        setSubmitting(false);
    };

    if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><Loader2 className="w-6 h-6 text-white animate-spin" /></div>;

    if (error) return (
        <div className="min-h-screen bg-background flex items-center justify-center p-6">
            <div className="bg-card border border-border/40 p-8 max-w-md text-center">
                <AlertCircle className="w-10 h-10 text-amber-300 mx-auto mb-4" />
                <h1 className="text-lg font-display font-bold text-white mb-2">Review not available</h1>
                <p className="text-sm text-gray-500">{error}</p>
            </div>
        </div>
    );

    const c = data.composite;
    const tsaRadar = (data.tsa_breakdown || []).map((d) => ({
        dimension: d.dimension,
        score: (d.final_score || 0) * 20,  // 0-5 → 0-100
    }));

    return (
        <div className="min-h-screen bg-background" data-testid="review-page">
            <header className="border-b border-border/40 bg-card">
                <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
                    <div>
                        <h1 className="text-sm font-display font-bold text-white">Realloc — Right of Review</h1>
                        <p className="text-xs text-gray-600 font-mono">{data.candidate?.name} &middot; {data.candidate?.job_title}</p>
                    </div>
                    <div className="text-xs text-gray-600 font-mono">{data.cycle?.name} &middot; {data.cycle?.methodology_version}</div>
                </div>
            </header>

            <main className="max-w-5xl mx-auto px-6 py-10 space-y-8">
                {/* Label + composite */}
                <div className="bg-card border border-border/40 p-6">
                    <p className="text-xs text-gray-600 uppercase tracking-wider mb-2">Your Capability Label</p>
                    <p className="text-3xl font-display font-bold text-white mb-1" data-testid="composite-label">{c.label}</p>
                    <p className="text-sm text-gray-500 leading-relaxed">The segment reflects your performance against <span className="text-white">{c.cadre}</span> role-classification expectations. Two candidates from different cadres with similar composites are not directly comparable — the cadre prefix makes this explicit.</p>
                </div>

                {/* Layer breakdown */}
                <div>
                    <p className="text-xs text-gray-600 uppercase tracking-wider mb-3">Layer Breakdown</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {[
                            { label: "Manager Validation", weight: "30%", value: c.mv },
                            { label: "Technical Assessment", weight: "25%", value: c.ta },
                            { label: "Hands-On Simulation", weight: "25%", value: c.hos },
                            { label: "Cadre-Band Analysis", weight: "20%", value: c.tsa },
                        ].map((l) => (
                            <div key={l.label} className="bg-card border border-border/40 p-4" data-testid={`layer-${l.label.replace(/\s+/g, '-')}`}>
                                <p className="text-xs text-gray-600 font-mono">{l.label}</p>
                                <p className="text-xs text-gray-700 font-mono mb-2">weight {l.weight}</p>
                                <p className="text-2xl font-display font-bold text-white font-mono">{l.value !== null ? l.value : '—'}</p>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Composite card + values floor */}
                <div className="bg-card border border-border/40 p-6">
                    <div className="flex items-center justify-between mb-2">
                        <p className="text-xs text-gray-600 uppercase tracking-wider">Capability Composite</p>
                        {c.values_floor_applied && (
                            <span className="text-xs font-mono text-amber-300 flex items-center gap-1"><Shield className="w-3 h-3" /> Values Floor Applied</span>
                        )}
                    </div>
                    <p className="text-5xl font-display font-bold text-white font-mono">{c.composite_final}<span className="text-lg text-gray-500 font-sans"> / 100</span></p>
                    {c.composite_unfloored !== c.composite_final && (
                        <p className="text-xs text-gray-500 mt-2 font-mono">Unfloored composite: {c.composite_unfloored}</p>
                    )}
                    {c.values_floor_applied && (
                        <p className="text-xs text-gray-500 mt-3 leading-relaxed">The Values Floor Rule applies when values-dimension scores fall below the workspace's configured threshold. It caps the composite irrespective of technical strength. Values scores are drawn from your manager's validation.</p>
                    )}
                </div>

                {/* TSA radar */}
                {tsaRadar.length > 0 && (
                    <div className="bg-card border border-border/40 p-6">
                        <p className="text-xs text-gray-600 uppercase tracking-wider mb-4">Cadre-Band Dimension Breakdown</p>
                        <div className="h-72">
                            <ResponsiveContainer width="100%" height="100%">
                                <RadarChart data={tsaRadar}>
                                    <PolarGrid stroke="#2a2a2a" />
                                    <PolarAngleAxis dataKey="dimension" tick={{ fill: '#bbb', fontSize: 11 }} />
                                    <PolarRadiusAxis domain={[0, 100]} tick={{ fill: '#666', fontSize: 10 }} />
                                    <Radar dataKey="score" stroke="#fff" fill="#fff" fillOpacity={0.18} />
                                </RadarChart>
                            </ResponsiveContainer>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                            {data.tsa_breakdown.map((d) => (
                                <div key={d.dimension} className="border border-border/40 p-3">
                                    <div className="flex items-center justify-between mb-1">
                                        <p className="text-sm text-white">{d.dimension}</p>
                                        <p className="text-xs font-mono text-gray-400">{(d.final_score * 20).toFixed(0)}/100</p>
                                    </div>
                                    <p className="text-xs text-gray-500 leading-relaxed">{d.rationale}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Advisory flags */}
                <div>
                    <p className="text-xs text-gray-600 uppercase tracking-wider mb-3">Advisory Flags</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {data.flags.map((f) => (
                            <div key={f.code} className={`border p-4 ${f.triggered ? "border-amber-500/40" : "border-border/40"}`} data-testid={`flag-${f.code}`}>
                                <div className="flex items-center justify-between mb-1">
                                    <p className="text-sm font-mono text-white">{f.code} &middot; <span className="text-gray-400 font-sans">{FLAG_EXPLANATIONS[f.code]?.name}</span></p>
                                    <span className={`text-xs font-mono ${f.triggered ? "text-amber-300" : "text-gray-600"}`}>{f.triggered ? "Triggered" : "Considered"}</span>
                                </div>
                                <p className="text-xs text-gray-500 leading-relaxed">{FLAG_EXPLANATIONS[f.code]?.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Per-response rationales + review entry */}
                {data.review_items?.length > 0 && (
                    <div>
                        <p className="text-xs text-gray-600 uppercase tracking-wider mb-3">Response-Level Rationales</p>
                        <p className="text-xs text-gray-500 mb-4 leading-relaxed">Each response below shows your answer, the final score (0–5 rubric), and the scorer's rationale. If you believe a score is factually unsupported or misses specific evidence in your submission, mark it for review and describe which evidence you'd like Realloc to reconsider.</p>
                        <div className="space-y-3">
                            {data.review_items.map((it) => {
                                const qid = it.question_id;
                                const isContested = qid in contested;
                                return (
                                    <div key={qid} className="bg-card border border-border/40 p-5" data-testid={`review-item-${qid}`}>
                                        <div className="flex items-start justify-between mb-2">
                                            <div className="flex-1">
                                                <p className="text-xs text-gray-600 font-mono mb-1">{it.module_name} &middot; {it.layer}</p>
                                                <p className="text-sm text-gray-200 leading-relaxed">{it.prompt}</p>
                                            </div>
                                            <div className="text-right ml-4">
                                                <p className="text-xs text-gray-600 font-mono">final</p>
                                                <p className="text-xl font-display font-bold text-white font-mono">{it.final_score}</p>
                                            </div>
                                        </div>
                                        <div className="mt-3 bg-muted border border-border/40 p-3">
                                            <p className="text-xs text-gray-600 mb-1">Your response</p>
                                            <p className="text-sm text-gray-300 whitespace-pre-wrap">{it.response_text || '(blank)'}</p>
                                        </div>
                                        <div className="mt-2 border-l-2 border-border/40 pl-3 py-1">
                                            <p className="text-xs text-gray-500 italic leading-relaxed">{it.rationale}</p>
                                        </div>
                                        {!data.already_submitted && (
                                            <div className="mt-3">
                                                {!isContested ? (
                                                    <button onClick={() => toggleContest(qid, "")} className="text-xs text-gray-400 hover:text-white" data-testid={`contest-btn-${qid}`}>+ Mark for review</button>
                                                ) : (
                                                    <div className="space-y-2">
                                                        <textarea value={contested[qid]} onChange={(e) => toggleContest(qid, e.target.value)}
                                                            placeholder="Describe which specific evidence in your response supports a different score..."
                                                            className="w-full bg-muted border border-border/40 p-2 text-sm text-white placeholder-gray-600 focus:border-white/30 outline-none h-20 resize-none" />
                                                        <button onClick={() => toggleContest(qid, null)} className="text-xs text-gray-500 hover:text-white">Remove from review</button>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Submit panel */}
                {!data.already_submitted ? (
                    <div className="bg-card border border-border/40 p-5 sticky bottom-6">
                        <p className="text-xs text-gray-600 uppercase tracking-wider mb-3">Overall Narrative (optional)</p>
                        <textarea value={narrative} onChange={(e) => setNarrative(e.target.value)}
                            placeholder="Any overall context you'd like Realloc to consider alongside your contested items..."
                            className="w-full bg-muted border border-border/40 p-3 text-sm text-white placeholder-gray-600 focus:border-white/30 outline-none h-24 resize-none mb-3" data-testid="review-narrative" />
                        <div className="flex items-center justify-between">
                            <p className="text-xs text-gray-500">{Object.keys(contested).length} item(s) marked · submitting will trigger the RoR flag in your profile.</p>
                            <button onClick={submit} disabled={submitting || (Object.keys(contested).length === 0 && !narrative.trim())}
                                className="px-5 py-2.5 bg-white text-black text-sm font-semibold hover:bg-gray-300 disabled:opacity-50 flex items-center gap-2" data-testid="submit-review-btn">
                                {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting</> : <><Send className="w-4 h-4" /> Submit Right of Review</>}
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="bg-card border border-green-500/30 p-5 flex items-start gap-3" data-testid="review-submitted">
                        <CheckCircle2 className="w-5 h-5 text-green-400 mt-0.5" />
                        <div>
                            <p className="text-sm font-semibold text-white">Right of Review submitted</p>
                            <p className="text-xs text-gray-500 mt-1">Your submission is logged as status <span className="font-mono text-white">{data.status}</span>. Realloc will acknowledge and respond per the Customer Agreement.</p>
                        </div>
                    </div>
                )}

                <div className="border-t border-border/40 pt-6 text-xs text-gray-600 leading-relaxed">
                    <div className="flex items-start gap-2">
                        <Info className="w-3 h-3 mt-0.5" />
                        <p>Scoring uses the Realloc 0–5 evidence-specificity rubric. Rationales describe present-state evidence only — no inference of future behaviour, trajectory, or disposition. The full methodology is available on request.</p>
                    </div>
                </div>
            </main>
        </div>
    );
}
